<template>
    <section class="section">
        <div class="container-fluid">
            <div class="columns">
                <div class="column">
                    <h1 class="title">{{ translate("page.all_finished_fights.title") }}</h1>
                </div>
            </div>

            <div v-if="initDone && finishedFights.length > 0">
                <div class="mb-3" v-if="finishedFights.length > 0">
                    <QueryInput v-model="searchQuery" />
                </div>

                <div class="table-responsive">
                    <table class="table is-condensed is-hoverable is-fullwidth">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{{ translate("common.category") }}</th>
                                <th>{{ translate("common.round") }}</th>
                                <th>
                                    {{ translate("page.start_fights.competitor") }}
                                    #1
                                </th>
                                <th>
                                    {{ translate("page.start_fights.competitor") }}
                                    #2
                                </th>
                                <th>{{ translate("page.start_fights.winner") }}</th>
                                <th>{{ translate("common.mat") }}</th>
                                <th>{{ translate("page.start_fights.finished") }}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            <FightTableRow
                                :show-duplicated-pair="false"
                                v-for="(fight, index) in filteredFights"
                                :fight="fight"
                                :index="index"
                                :key="fight.id"
                            >
                                <template v-slot:afterCells>
                                    <td class="has-text-success">
                                        <template v-if="fight.winnerCompetitor">
                                            <span class="clarity-mask">
                                                {{ fight.winnerCompetitor.firstName }}
                                                {{ fight.winnerCompetitor.lastName }}
                                            </span>
                                            <br />
                                            <small v-if="fight.winnerCompetitor.academy">
                                                {{ fight.winnerCompetitor.academy }}
                                            </small>
                                        </template>
                                    </td>
                                    <td>
                                        {{ fight.matName }}
                                    </td>
                                    <td>
                                        {{ convertToTimezone(fight.finished ?? "", eventTimezone) }}
                                    </td>
                                    <td>
                                        <LoadingButton
                                            :is-loading="revertingInProgress === fight.id"
                                            class="button is-small"
                                            @click="onRevertResult(fight.id)"
                                        >
                                            {{ translate("page.start_fights.revert") }}
                                        </LoadingButton>
                                    </td>
                                </template>
                            </FightTableRow>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="columns" v-else-if="finishedFights.length === 0">
                <div class="column is-6 is-offset-3">
                    <Notification class="is-info">
                        <span class="icon">
                            <i class="fa fa-info-circle" />
                        </span>
                        <span>
                            {{ translate("page.start_fights.no_fights_played_at_all") }}
                        </span>
                    </Notification>
                </div>
            </div>
            <div class="columns is-centered is-mobile" v-else>
                <LoadingSpinner2 class="has-added-margin"></LoadingSpinner2>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import FightTableRow from "./FightTableRow.vue";
    import fightsFilter from "../Common/fightsFilter";
    import QueryInput from "../Common/QueryInput.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import { computed, onMounted, ref } from "vue";
    import fightsApi from "../Api/fights";
    import { handleError } from "../Common/errorHandler";
    import LoadingButton from "../Common/LoadingButton.vue";
    import Notification from "../Common/Notification.vue";
    import { OrganizerComplexFight } from "../Common/Fights/types";
    import { translate } from "../Common/translations";
    import { successToast } from "../Common/toasts";
    import { convertToTimezone } from "../Common/dateTimeUtils";

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
    }>();

    const searchQuery = ref("");
    const finishedFights = ref<OrganizerComplexFight[]>([]);
    const initDone = ref(false);
    const revertingInProgress = ref(0);

    const filteredFights = computed(() => {
        return fightsFilter.filter(finishedFights.value, searchQuery.value);
    });

    function fetchAllFinishedFights() {
        return fightsApi.getAllFinishedFights(props.eventId, true).then(response => {
            finishedFights.value = response.result;
        });
    }

    function revertResult(fightId) {
        return fightsApi.revertFightResult(props.eventId, fightId);
    }

    function onRevertResult(fightId) {
        const text = translate("page.start_fights.sure_to_revert") + " " + fightId + "?";
        if (!confirm(text)) {
            return;
        }
        revertingInProgress.value = fightId;

        return revertResult(fightId)
            .then(() => {
                successToast(translate("page.start_fights.fight_reverted"));
                return fetchAllFinishedFights();
            })
            .finally(() => {
                revertingInProgress.value = 0;
            })
            .catch(handleError);
    }

    onMounted(() => {
        return fetchAllFinishedFights().finally(() => {
            initDone.value = true;
        });
    });
</script>

<style scoped></style>
